import EventlevelAPI from '@/api/model/eventlevel';
import MenuInfoApi from '@/api/model/menu-info';
import FileApi from '@/api/common/file';
import ManagetypeApi from '@/api/model/managetype';
import ObjtypeApi from '@/api/model/objtype';
import { booleanDefine } from '@/utils/boolean';
import { isNumber } from '@/utils/number';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import basicinfoSetAPI from '@/api/model/basicinfo-set';

const common = {
  state: {
    userInfo: null,
    selectedMenu: {},
    selectedNode: null,
    selectedNodePath: [],
    activeTab: {},
    /**
     * 이벤트 등급 객체. eventlevelId를 key로 관리<br/>
     * eventlevelInfo[eventlevelId] 를 통해 해당 이벤트 등급의 등급명, 색상 정보를 가져올 수 있다.
     *
     * eventlevelInfo = {
     *  'DN': {
     *     eventlevelId: 'DN',
     *     eventlevelName: 'Down',
     *     eventlevelColorBg: ..,
     *      eventlevelColorText: ..,
     *      orderbyIndex: ..,
     *      weatherCode: ..
     *   },
     *   ...
     * }
     * @type {Object}
     */
    eventlevelInfo: {},
    eventlevelList: [],
    showFullscreenLayer: false,
    keepVModalClass: null,
    menuInfo: [],
    activeManagetypes: [],
    showSettingView: false,
    /**
     * 업로드 파일 최대 사이즈 (MB)
     * @type {Number}
     */
    fileMaxMbSize: 32,
    // 고객사 ID
    customerId: 'DEFAULT',
    /**
     * 사용자 이미지 정보
     * @type {Object}
     */
    customimage: {},
    availableSensorList: [],
    analysisTimetype: 'HIST',
  },
  mutations: {
    setSelectedMenu: (state, menu) => {
      state.selectedMenu = menu;
    },
    setSelectedNode: (state, node) => {
      state.selectedNode = node;
    },
    setSelectedNodePath: (state, path) => {
      state.selectedNodePath = path;
    },
    setActiveTab: (state, tab) => {
      state.activeTab = tab;
    },
    loadEventlevelInfo: (state, eventlevelInfo) => {
      state.eventlevelInfo = eventlevelInfo;
    },
    loadEventlevelList: (state, eventlevels) => {
      state.eventlevelList = eventlevels;
    },
    setShowFullScreenLayer: (state, showFullscreenLayer) => {
      state.showFullscreenLayer = showFullscreenLayer;
    },
    setUserInfo: (state, userInfo) => {
      state.userInfo = userInfo;
    },
    setKeepVModalClass: (state, keepVModalClass) => {
      state.keepVModalClass = keepVModalClass;
    },
    loadMenuInfo: (state, menuInfo) => {
      state.menuInfo = menuInfo;
    },
    loadActiveManagetypes: (state, activeManagetypes) => {
      state.activeManagetypes = activeManagetypes;
    },
    setShowSettingView: (state, showSettingView) => {
      state.showSettingView = showSettingView;
    },
    setCustomerId: (state, customerId) => {
      state.customerId = customerId;
    },
    loadAvailableSensorList: (state, availableSensorList) => {
      state.availableSensorList = availableSensorList;
    },
    /**
     * 업로드 파일 최대 사이즈를 설정한다.
     * @author Ju
     * @param  {[type]} state [description]
     * @param  {Number} value 파일 최대 사이즈 (MB)
     */
    setFileMaxMbSize: (state, value) => {
      state.fileMaxMbSize = value;
    },
    /**
     * 논리 그룹 트리 데이터를 설정한다.
     * @author Ju
     * @param  {[type]} state [description]
     * @param  {[type]} value [description]
     */
    setLogicalTreeData: (state, value) => {
      state.logicalTreeData = value;
    },
    /**
     * 물리 그룹 트리 데이터를 설정한다.
     * @author Ju
     * @param  {[type]} state [description]
     * @param  {[type]} value [description]
     */
    setPhysicalTreeData: (state, value) => {
      state.physicalTreeData = value;
    },
    setLogicalTreeLoading: (state, value) => {
      state.logicalTreeLoading = value;
    },
    setPhysicalTreeLoading: (state, value) => {
      state.phyiscalTreeLoading = value;
    },
    setCustomimage(state, customimage) {
      state.customimage = customimage;
    },
    setAnalysisTimetype: (state, value) => {
      state.analysisTimetype = value;
    },
  },
  actions: {
    setSelectedMenu({ commit }, menu) {
      commit('setSelectedMenu', menu);
    },
    setSelectedNode({ commit }, node) {
      commit('setSelectedNode', node);
    },
    setSelectedNodePath({ commit }, path) {
      commit('setSelectedNodePath', path);
    },
    setActiveTab({ commit }, tab) {
      commit('setActiveTab', tab);
    },
    setTreeEditMode({ commit }, isEditing) {
      commit('setTreeEditMode', isEditing);
    },
    loadEventlevelInfo({ commit }) {
      EventlevelAPI.getList(booleanDefine.TRUE_Y).then((response) => {
        if (response.data.success) {
          const eventlevels = response.data.data;
          const eventlevelInfo = {};

          forEach(eventlevels, (item) => {
            eventlevelInfo[item.eventlevelId] = item;
          });

          commit('loadEventlevelList', eventlevels);
          commit('loadEventlevelInfo', eventlevelInfo);
        }
      });
    },
    openFullScreenLayer({ commit }) {
      document.body.className += ' show-full-screen-layer ';
      const vModalEl = document.querySelector('div.v-modal');
      if (vModalEl !== null) {
        let vModalClass = '';
        forEach(vModalEl.classList, (item) => {
          if (item !== 'v-modal') {
            vModalClass = `${vModalClass} ${item}`;
          }
        });

        if (!isEmpty(vModalClass)) {
          vModalEl.className = 'v-modal';
          commit('setKeepVModalClass', vModalClass);
        }
      }
      commit('setShowFullScreenLayer', true);
    },
    closeFullScreenLayer({ state, commit }) {
      document.body.className = document.body.className.replace('show-full-screen-layer', ' ').trim();
      if (!isEmpty(state.keepVModalClass)) {
        const vModalEl = document.querySelector('div.v-modal');
        if (vModalEl !== null) {
          vModalEl.className = `v-modal ${state.keepVModalClass}`;
        }
        commit('setKeepVModalClass', null);
      }
      commit('setShowFullScreenLayer', false);
    },
    setUserInfo({ commit }, userInfo) {
      commit('setUserInfo', userInfo);
    },
    loadMenuInfo({ commit }) {
      MenuInfoApi.getList().then((response) => {
        if (response.data.success) {
          commit('loadMenuInfo', response.data.data);
        }
      });
    },
    loadActiveManagetypes({ commit }) {
      ManagetypeApi.getActiveManagetypeList().then((response) => {
        if (response.data.success) {
          commit('loadActiveManagetypes', response.data.data);
        }
      });
    },
    showSettingView({ commit }) {
      commit('setShowSettingView', true);
    },
    closeSettingView({ commit }) {
      commit('setShowSettingView', false);
    },
    /**
     * 파일 최대 사이즈를 설정한다.
     * @author Ju
     * @param  {[type]} commit [description]
     */
    loadFileMaxMbSize({ commit }) {
      FileApi.getFileMaxMbSize().then((response) => {
        if (response.success && isNumber(response.data.data)) {
          commit('setFileMaxMbSize', response.data.data);
        }
      });
    },
    loadCustomerId({ commit }) {
      basicinfoSetAPI
        .getBasicinfoSet({
          basicinfoId: 'CUSTOMER_ID',
          basicinfoSubid: 'ID',
        })
        .then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            commit('setCustomerId', response.data.data.basicinfoSubvalueString);
          }
        });
    },
    loadAvailableSensorList({ commit }) {
      ObjtypeApi.getAvailableSensorList().then((response) => {
        if (response.data.success) {
          commit('loadAvailableSensorList', response.data.data);
        }
      });
    },
  },
};

export default common;



// WEBPACK FOOTER //
// ./src/store/modules/common.js